import { queryOptions } from "@tanstack/react-query";
import type { ScopedBinding } from "@/app/types";
import type { ApiClient } from "@/lib/api";
import { getEngineApi } from "@/lib/api";
import type { OrgProjectScope } from "@/lib/resource-context";
import type { Hook, HookListResponse } from "../types";
import { hooksKeys } from "./query-keys";

export const fetchHooks = async ({
  client,
  hookPoint,
  pluginId,
}: {
  client: ApiClient;
  hookPoint?: string;
  pluginId?: string;
}): Promise<Hook[]> => {
  const params: Record<string, string> = {};
  if (hookPoint) {
    params.hook_point = hookPoint;
  }
  if (pluginId) {
    params.plugin_id = pluginId;
  }
  const { data } = await client.get<HookListResponse | Hook[]>("/hooks", {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  if (Array.isArray(data)) {
    return data;
  }
  return data?.items ?? [];
};

export const hooksQueryOptions = ({
  binding = "project",
  hookPoint,
  pluginId,
  scope,
}: {
  binding?: ScopedBinding;
  hookPoint?: string;
  pluginId?: string;
  scope: OrgProjectScope;
}) => {
  const client = getEngineApi(scope, binding);

  return queryOptions<Hook[]>({
    queryKey: [...hooksKeys.list({ binding, scope }), hookPoint, pluginId],
    queryFn: () => fetchHooks({ client, hookPoint, pluginId }),
  });
};

export const fetchHook = async ({
  client,
  id,
  binding = "project",
}: {
  client: ApiClient;
  id: string;
  binding?: ScopedBinding;
}): Promise<Hook> => {
  const { data } = await client.get<Hook>(`/hooks/${id}`, {
    params: { binding_type: binding },
  });
  return data;
};

export const hookQueryOptions = ({
  id,
  binding = "project",
  scope,
}: {
  id: string;
  binding?: ScopedBinding;
  scope: OrgProjectScope;
}) => {
  const client = getEngineApi(scope, binding);

  return queryOptions<Hook>({
    queryKey: hooksKeys.detail({ binding, scope, id }),
    queryFn: () => fetchHook({ client, id, binding }),
  });
};
